/*
  Таблица исполнительных документов — §3.6.

  🔴 Раздел стал таблицей 22.09.2026 по решению владельца, следом за радаром и
  тем же устройством: плитки с суммами, переключатель и одна таблица. До этого
  он был лентой карточек, в каждой из которых стояло правовое объяснение срока
  целиком, поле суда и форма события; одна карточка занимала две трети экрана.

  Восемь колонок: состояние, дело и суд, счёт, присуждено, взыскано, осталось,
  срок, действия. Правовое объяснение срока не выброшено - оно под значком
  «как?» рядом со сроком: это доказательство даты, и прятать его насовсем
  нельзя (§4.5.2).

  Компонент ничего не грузит и ничего не сортирует: порядок, отбор и страницы задаёт сервер.
*/

import { Link } from 'react-router-dom'

import { date, days as daysWord, money } from '../api/format.js'
import { Tag } from './Common.jsx'
import InfoHint from './InfoHint.jsx'
import PageBar from './PageBar.jsx'

export default function EnforcementTable({ rows, bar, canAct, onEvent, onIndexation, busyId }) {
  // 21.10.2026: исполнительных документов у заказчика сотни - страницу режет сервер
  // (`EnforcementList.jsx`), здесь строки страницы и общая панель.
  return (
    <>
      <table className="table table--fixed table--enf">
        <thead>
          <tr>
            <th>Состояние</th>
            <th>Дело и суд</th>
            <th>Счёт</th>
            <th className="table__num">Присуждено</th>
            <th className="table__num">Взыскано</th>
            <th className="table__num">Осталось</th>
            <th>Срок</th>
            <th>{canAct ? 'Действия' : 'Перейти'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item) => (
            <Row
              key={item.id}
              item={item}
              canAct={canAct}
              onEvent={onEvent}
              onIndexation={onIndexation}
              busy={busyId === item.id}
            />
          ))}
        </tbody>
      </table>
      <PageBar {...bar} what="документов" />
    </>
  )
}

function Row({ item, canAct, onEvent, onIndexation, busy }) {
  return (
    // `data-label` - подпись колонки для узкого экрана: там строка
    // разворачивается в карточку, заголовки не видны. У первой ячейки подписи
    // нет намеренно: состояние читается само и служит заголовком карточки.
    <tr>
      <td>
        <Tag kind={item.is_idle ? 'warn' : 'calm'}>{item.state_title}</Tag>
        {item.channel_title ? <div className="muted">{item.channel_title}</div> : null}
      </td>
      <td data-label="Дело и суд">
        <span className="enf__case">№ {item.case_number}</span>
        {/*
          Суд назван прямо в строке: заявление об индексации подаётся именно в
          него, и приказ без суда собрать его не даёт. Пустое место здесь - не
          украшение, а работа, которую надо доделать, поэтому пропуск назван
          словами и окрашен как предупреждение.
        */}
        <div className="muted">
          {item.court_name ? (
            item.court_name
          ) : item.state === 'cancelled' ? (
            'Суд не указан'
          ) : (
            <span className="enf__gap">суд не указан - заявление об индексации не собрать</span>
          )}
        </div>
      </td>
      <td data-label="Счёт">
        <Link to={`/accounts/${item.account_id}`}>
          {item.account_number ? `№ ${item.account_number}` : 'счёт'}
        </Link>
        {item.account_address ? <div className="muted">{item.account_address}</div> : null}
      </td>
      <td className="table__num enf__sum" data-label="Присуждено">
        {money(item.awarded_amount)}
      </td>
      <td className="table__num" data-label="Взыскано">
        {money(item.recovered_amount)}
      </td>
      <td className="table__num enf__sum" data-label="Осталось">
        {money(item.remaining_amount)}
      </td>
      <td data-label="Срок">
        <Deadline deadline={item.deadline} />
      </td>
      <td data-label={canAct ? 'Действия' : 'Перейти'}>
        <div className="enf__acts">
          {/*
            🔴 У отменённого приказа действий нет вовсе (14.09.2026, найдено
            осмотром). Отметка события вернула бы его в живое состояние - вид
            события задаёт состояние документа, - а заявление об индексации
            собралось бы по акту, который ничего не присуждает: статья 208
            индексирует ПРИСУЖДЁННЫЕ суммы. Сервер отказывает и в том, и в
            другом; действие, которое заведомо отклонят, обещает невозможное.

            Причина остаётся на виду под значком: пустая колонка читалась бы как
            недоделанный экран.
          */}
          {canAct && item.state !== 'cancelled' ? (
            <>
              <button className="enf__act no-hover-lift" type="button" onClick={() => onEvent(item)}>
                Отметить событие
              </button>
              {/*
                🔴 Указание владельца 22.09.2026: если что-то сделать нельзя,
                действие не показывается вовсе. Без суда сервер заявление об
                индексации не соберёт и откажет - кнопка обещала бы то, чего не
                будет, и человек узнавал бы об этом только нажав. Причина стоит
                строкой выше, в колонке «Дело и суд», и там же сказано, что
                делать: указать суд.
              */}
              <Indexation item={item} busy={busy} onIndexation={onIndexation} />
            </>
          ) : null}
          {item.state === 'cancelled' ? (
            <span className="muted">
              действий нет{' '}
              <InfoHint
                title="Почему"
                text="Приказ отменён: события по нему не отмечаются и заявление об индексации не собирается. Требование заявляется исковым порядком - счёт уже переведён в эту стадию."
              />
            </span>
          ) : null}
          <Link className="enf__act" to={`/accounts/${item.account_id}`}>
            Карточка счёта
          </Link>
        </div>
      </td>
    </tr>
  )
}

/*
  Заявление об индексации — §6.8.4.

  🔴 Указание владельца 22.09.2026, два условия сразу, и оба про одно: не звать
  к работе, которой не будет.

  1. **Без суда** сервер заявление не соберёт и откажет. Причина стоит строкой
     выше, в колонке «Дело и суд», и там же сказано, что делать.
  2. **Невыгодная индексация** не предлагается: она пропорциональна
     присуждённой сумме, и по небольшому долгу накапливается меньше, чем стоит
     подача. Считает это сервер - у него тарифы заказчика; здесь только показ.
     Сумма при этом не прячется: она названа, и рядом объяснение, почему
     подавать незачем. Спрятать деньги совсем значило бы решить за человека.

  Пустое `indexation` - считать нечем или нечего: ряд индексов не покрывает
  день вынесения либо индексация равна нулю. Тогда действия нет вовсе.
*/
function Indexation({ item, busy, onIndexation }) {
  const offer = item.indexation
  if (!offer || !item.court_name || !onIndexation) return null
  if (offer.is_worth === false) {
    return (
      <span className="muted">
        индексация {money(offer.amount)} - подавать невыгодно{' '}
        <InfoHint title="Почему" text={offer.explanation} />
      </span>
    )
  }
  return (
    <button
      className="enf__act no-hover-lift"
      type="button"
      disabled={busy}
      onClick={() => onIndexation(item)}
    >
      Заявление об индексации
    </button>
  )
}

/*
  Срок предъявления: метка с остатком дней, дата под ней и объяснение под
  значком «как?». Объяснение приходит с сервера готовым текстом - правило, по
  которому оно собрано, правовое (удержание срок продлевает, возврат по
  заявлению взыскателя урезает), и пересказывать его на экране нельзя.

  🔴 Совет (`deadline.advice`) остаётся ВНУТРИ подсказки, вместе с расчётом.
  Решение владельца 01.10.2026 по макету из четырёх вариантов: оставить как
  есть. Отдельной строкой во всю ширину, двумя значками и переносом в карточку
  счёта - рассмотрены и отклонены.

  Пробовать поставить его блоком в колонку «Срок» не нужно: проверено осмотром
  в тот же день. Колонка имеет 137 пикселей при восьми колонках таблицы, а у
  `.table--fixed td` стоит `overflow-wrap: anywhere` - длинное значение рвётся
  где угодно, лишь бы не вылезти за край. В замере даже «Можно предъявлять.»
  заняло 113 пикселей ширины и 114 высоты, рассыпавшись на «Мож / но / пред /
  ъявл / ять», а строка таблицы выросла с 87 до 290 пикселей. Советы же бывают
  в три предложения.
*/
function Deadline({ deadline }) {
  if (deadline.expires_on === null) {
    return <span className="muted">{deadline.explanation}</span>
  }
  const tone = deadline.is_expired ? 'burn' : deadline.is_burning ? 'warn' : 'calm'
  return (
    <>
      <Tag kind={tone}>
        {deadline.is_expired ? 'срок истёк' : `осталось ${daysWord(deadline.days_left ?? 0)}`}
      </Tag>
      <div className="muted">
        до {date(deadline.expires_on)}{' '}
        <InfoHint text={`${deadline.explanation} ${deadline.advice}`} title="Как считан срок" />
      </div>
    </>
  )
}
